
import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Avatar,
  Tag,
  Space,
  Button,
  Typography,
  Tooltip,
  Checkbox,
  Popconfirm,
  Row,
  Col,
  Divider,
  Badge,
  Collapse,
  message,
  Input,
  Select,
  AutoComplete,
  Modal,
  Timeline
} from "antd";
import {
  PhoneOutlined,
  MailOutlined,
  CarOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  DollarCircleOutlined,
  MobileOutlined,
  CommentOutlined,
  CalendarOutlined,
  GlobalOutlined,
  UserOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  WarningOutlined,
  DeleteOutlined,
  TagOutlined,
  FileTextOutlined,
  NumberOutlined,
  SearchOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  StarOutlined,
  SendOutlined,
  CopyOutlined,
  DatabaseOutlined,
  LinkOutlined,
  KeyOutlined,
  BarChartOutlined,
  ShopOutlined,
  SecurityScanOutlined,
  SyncOutlined,
  ExportOutlined,
  FireOutlined,
  CrownOutlined,
  TeamOutlined,
  InboxOutlined,
  BellOutlined,
  FlagOutlined,
  BookOutlined,
  SolutionOutlined,
  SwapOutlined,
  CheckOutlined,
  CloseOutlined,
  UserSwitchOutlined
} from "@ant-design/icons";
import TransferLeadModal from '../../Pages/LeadDetails/components/TabComponents/TransferLeadModel.jsx';
import EmailTemplateModal from '../../Pages/LeadDetails/components/EmailTemplateModal.jsx';
import API from '../../API/API';
import LeadFullDetailModal from '../../Components/LeadFullDetailModal.jsx';
import { formatUSPhone } from "../../Utils.js"
import { formatPT, zoneAbbrPT } from "../../utils/usTime";
import { resolveLeadTimeZone, zoneAbbr, zoneTime, zoneColor, useMinuteClock } from "../../utils/leadTimezone";
import { UserCheck } from "lucide-react";
// Removed useParams — not needed here

const { Text, Title } = Typography;
const { Panel } = Collapse;

const phoneValidationBadgeBaseStyle = {
  borderRadius: 10,
  fontSize: 10,
  lineHeight: '16px',
  padding: '0 7px',
  margin: 0,
  fontWeight: 500,
};

const NUMBER_FORMAT_BADGE_STYLES = {
  valid: { background: '#ecfdf5', borderColor: '#bbf7d0', color: '#047857' },
  message: { background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' },
  unknown: { background: '#f8fafc', borderColor: '#e2e8f0', color: '#475569' },
};

const getPhoneValidationBadgeStyle = (value, styleMap, fallbackKey) => ({
  ...phoneValidationBadgeBaseStyle,
  ...(styleMap[value] || styleMap[fallbackKey]),
});

const phoneValidationContainerStyle = {
  marginTop: 4,
  marginLeft: 24,
  maxWidth: 'calc(100% - 24px)',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 4,
};

const phoneValidationFormatRowStyle = {
  width: '100%',
  minWidth: 0,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
};

const phoneValidationFormatBadgeStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  padding: '2px 7px',
};

const phoneValidationCompactBadgeRowStyle = {
  maxWidth: '100%',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
  paddingLeft: 17,
};

const getLatestNoteLine = (comment) => (
  typeof comment === 'string'
    ? comment
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .pop() || ''
    : ''
);

const REVIEW_LEAD_RECOVERED_BY_PREFIX = 'Recovered from Review Lead by';
const REVIEW_LEAD_RECOVERED_ASSIGNED_PREFIX = 'Recovered from Review Lead and assigned to';

const getCommentText = (entry = {}) => (
  typeof entry.comment === 'string' ? entry.comment.trim() : ''
);

const isAdminOnlyLeadStageHistoryEntry = (entry = {}) => (
  entry.isAdminOnly === true ||
  getCommentText(entry).startsWith(REVIEW_LEAD_RECOVERED_BY_PREFIX)
);

const isAdminOnlyLeadPageCommentEntry = (entry = {}) => (
  entry.isAdminOnly === true ||
  getCommentText(entry).startsWith(REVIEW_LEAD_RECOVERED_ASSIGNED_PREFIX)
);

const filterAdminOnlyEntries = (entries, isAdmin, matcher) => {
  const list = Array.isArray(entries) ? entries : [];
  return isAdmin ? list : list.filter((entry) => !matcher(entry));
};

// Cards that show the same service agent share one /auth/userById request
// while it is in flight, so a page of cards makes one lookup per agent instead
// of one per card. Nothing is kept once the request settles, so every load of
// the list still reads the agent fresh, as before.
const serviceAgentRequests = new Map();

const getServiceAgentById = (serviceAgentId) => {
  const key = String(serviceAgentId);

  if (!serviceAgentRequests.has(key)) {
    serviceAgentRequests.set(
      key,
      API.GetUserById(serviceAgentId).finally(() => serviceAgentRequests.delete(key))
    );
  }

  return serviceAgentRequests.get(key);
};

function LeadCard({
  record,
  navigate,
  loginAgent,
  onDelete,
  selectedRowKeys,
  setSelectedRowKeys,
  isGlobalSearchResult = false,
  onRefresh = { loadLeads },
  onBeforeNavigateToLeadDetails
}) {
  // Use record as lead data
  const lead = record;

  const personalInfo = lead?.personalInfo || {};
  const phoneValidation = lead?.phoneValidation || {};
  const locationInfo = lead?.locationInfo || {};
  const vehicleInfo = lead?.vehicleInfo || {};
  const browserInfo = lead?.browserInfo || {};
  const assignmentMetadata = lead?.assignmentMetadata || {};
  const auditTrail = lead?.audit || [];
  const transferHistory = lead?.transferHistory || [];
  const quotes = lead?.quotes || [];
  const advancePayments = lead?.advancePayments || [];
  const zohoSign = lead?.zohoSign || {};
  const isAdminUser = loginAgent?.role === "ADMIN";
  const leadStageHistory = useMemo(
    () => filterAdminOnlyEntries(lead?.leadStageHistory, isAdminUser, isAdminOnlyLeadStageHistoryEntry),
    [lead?.leadStageHistory, isAdminUser]
  );
  const visibleLeadPageComment = useMemo(
    () => filterAdminOnlyEntries(lead?.leadPageComment, isAdminUser, isAdminOnlyLeadPageCommentEntry),
    [lead?.leadPageComment, isAdminUser]
  );

  // Customer local time next to the name (ZIP first, then browser zone).
  const leadZip = lead?.personalInfo?.zipCode;
  const leadBrowserZone = lead?.browserInfo?.timeZone;
  const customerTz = useMemo(
    () => resolveLeadTimeZone({ personalInfo: { zipCode: leadZip }, browserInfo: { timeZone: leadBrowserZone } }),
    [leadZip, leadBrowserZone]
  );
  const clockNow = useMinuteClock(Boolean(customerTz));
  const customerTzAbbr = customerTz ? zoneAbbr(customerTz.zone, new Date(clockNow)) : '';
  const customerTzTime = customerTz ? zoneTime(customerTz.zone, new Date(clockNow)) : '';

  const leadStage = lead?.leadStage || "Unknown";
  const domain = lead?.domain || "";
  const zohoStatus = lead?.zohoStatus;
  const totalAmount = lead?.totalAmount ?? 0;
  const paymentStatus = lead?.paymentStatus || "Pending";
  const agentName = lead?.agentName;
  const createdAt = lead?.createdAt;
  const assignedDate = lead?.assignedDate;
  const updatedAt = lead?.updatedAt;
  const salesMadeHistoryEntry = leadStageHistory
    .filter(history => history?.stage === "Sales Made" && history?.changedAt)
    .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))[0];
  const salesMadeDate = leadStage === "Sales Made"
    ? salesMadeHistoryEntry?.changedAt || lead?.orderDate || null
    : null;
  const missedFollowUp = lead?.missedFollowUp;
  // Follow-up times are picked as US Pacific times, so they are shown in
  // Pacific on any PC, as on the My Follow-Ups page. Same style as the card's
  // other dates, without seconds.
  const followUpAt = leadStage === "Follow-Up"
    ? formatPT(lead?.followUpDate, 'MMM DD, YYYY, hh:mm A', null)
    : null;
  const isFollowUpOverdue = Boolean(followUpAt) && new Date(lead.followUpDate).getTime() < Date.now();
  const isExistingCustomer = lead?.isExistingCustomer;
  const queueLead = lead?.queueLead;
  const latestComment = getLatestNoteLine(leadStageHistory[leadStageHistory.length - 1]?.comment);
  const quotedPrice = lead?.quotedValue?.[lead.quotedValue?.length - 1]?.price;
  // A lead keeps its quote when it moves from Quoted to Follow-Up, so the
  // Follow-Up card shows the amount too. The payment tag stays off that card
  // until a payment is on record, so a quoted follow-up doesn't read as "Unpaid".
  const hasQuote = lead?.quotedValue?.length > 0 || totalAmount > 0;
  const showQuotedAmount = (leadStage === "Quoted" || leadStage === "Follow-Up") && hasQuote;
  const showPaymentTag = leadStage !== "Follow-Up" || advancePayments.length > 0;
  const tierType = lead?.tierType;
  const tierLane = lead?.tierLane;
  const tierLaneLabel = lead?.tierLaneLabel;
  // The backend labels the lane with the domain group's name, so a group an
  // admin renames in Domain Mapping reads correctly here. The raw key stands in
  // only when the label is missing.
  const tierLaneDisplayLabel = tierLaneLabel || tierLane;
  const status = lead?.status;
  const dedupeKey = lead?.dedupeKey;
  const score = lead?.score;
  const queuedAt = lead?.queuedAt;
  const queuePriority = lead?.queuePriority;
  const leadType = lead?.leadType;
  const isInboundLead = leadType === "inbound";
  const createdBy = lead?.createdBy;
  const createdByName = typeof createdBy === "object" && createdBy
    ? `${createdBy.firstName || ""} ${createdBy.lastName || ""}`.trim() || createdBy.userName || createdBy.email
    : "";
  const secondaryStatus = lead?.secondaryStatus;
  const notificationRead = lead?.notificationRead;
  const paidAmount = lead?.paidAmount || 0;
  const balanceAmount = lead?.balanceAmount || 0;
  const documentIds = zohoSign?.documentIds || [];
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isFullDetailModalVisible, setIsFullDetailModalVisible] = useState(false);
  const [senderAgentId, setSenderAgentId] = useState(null);
  const [agentMap, setAgentMap] = useState({});
  const [agentFullMap, setAgentFullMap] = useState({});
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [isEditingVin, setIsEditingVin] = useState(false);
  const [vinValue, setVinValue] = useState(vehicleInfo?.vin || '');
  const [savingVin, setSavingVin] = useState(false);
  // Add these near your VIN states (around line 60-70)
  const [isEditingSpec, setIsEditingSpec] = useState(false);
  const [specValue, setSpecValue] = useState(vehicleInfo?.specification || '');
  const [savingSpec, setSavingSpec] = useState(false);
// Add warranty states
const [isEditingWarranty, setIsEditingWarranty] = useState(false);
const [warrantyValue, setWarrantyValue] = useState(lead?.warranty || '');
const [savingWarranty, setSavingWarranty] = useState(false);
// Add these state variables with your other states (around line 60-70)
const [serviceAgentName, setServiceAgentName] = useState(null);
const [serviceAgentDetails, setServiceAgentDetails] = useState(null);
const [loadingServiceAgent, setLoadingServiceAgent] = useState(false);
// Add these state variables
const [isServiceAgentModalVisible, setIsServiceAgentModalVisible] = useState(false);
const [availableServiceAgents, setAvailableServiceAgents] = useState([]);
const [selectedServiceAgentId, setSelectedServiceAgentId] = useState(null);
const [assigningServiceAgent, setAssigningServiceAgent] = useState(false);
const [loadingServiceAgents, setLoadingServiceAgents] = useState(false);
// Starts with the lead's comments, so a new card doesn't render a second
// time just to copy them in.
const [allComments, setAllComments] = useState(visibleLeadPageComment);
const [isCommentsModalVisible, setIsCommentsModalVisible] = useState(false);
const [newComment, setNewComment] = useState('');
const [addingComment, setAddingComment] = useState(false);

// Get warranty color
const getWarrantyColor = (warranty) => {
  const colors = {
    "30 Days": "green",
    "60 Days": "cyan",
    "90 Days": "blue",
    "120 Days": "geekblue",
    "150 Days": "purple",
    "180 Days": "magenta",
    "1 Year": "gold",
    "2 Years": "orange",
    "3 Years": "volcano",
    "4 Years": "red",
    "5 Years": "volcano",
    "No Warranty": "default",
  };
  return colors[warranty] || "default";
};
useEffect(() => {
    // Keep in step when the lead's comments change. On mount this is the
    // same array the state started with, so React skips the re-render.
    setAllComments(visibleLeadPageComment);
}, [visibleLeadPageComment]);


useEffect(() => {
    const fetchServiceAgent = async () => {
        const serviceAgentId = lead?.serviceHandledBy?._id || lead?.serviceHandledBy;
        
        if (!serviceAgentId) {
            setServiceAgentName(null);
            setServiceAgentDetails(null);
            return;
        }

        // If serviceHandledBy is already populated with user object
        if (lead?.serviceHandledBy && typeof lead.serviceHandledBy === 'object' && lead.serviceHandledBy.firstName) {
            const fullName = `${lead.serviceHandledBy.firstName || ''} ${lead.serviceHandledBy.lastName || ''}`.trim();
            setServiceAgentName(fullName || lead.serviceHandledBy.userName || 'Service Agent');
            setServiceAgentDetails(lead.serviceHandledBy);
            return;
        }

        // Otherwise fetch from API
        setLoadingServiceAgent(true);
        try {
            const response = await getServiceAgentById(serviceAgentId);
            if (response?.success && response?.data) {
                const user = response.data;
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                setServiceAgentName(fullName || user.userName || 'Service Agent');
                setServiceAgentDetails(user);
            } else {
                setServiceAgentName('Service Agent');
            }
        } catch (error) {
            console.error('Error fetching service agent:', error);
            setServiceAgentName('Service Agent');
        } finally {
            setLoadingServiceAgent(false);
        }
    };

    fetchServiceAgent();
}, [lead?.serviceHandledBy]);

useEffect(() => {
    const fetchServiceAgents = async () => {
        setLoadingServiceAgents(true);
        try {
            const response = await API.userGetAll();
            if (response?.success) {
                // Filter users with SERVICE-AGENT role
                const serviceAgentsList = response.data.filter(
                    user => user.role === "SERVICE-AGENT" || user.role === "SUPPORT-AGENT"
                );
                setAvailableServiceAgents(serviceAgentsList);
            }
        } catch (error) {
            console.error('Error fetching service agents:', error);
            message.error('Failed to load service agents');
        } finally {
            setLoadingServiceAgents(false);
        }
    };

    if (isServiceAgentModalVisible) {
        fetchServiceAgents();
    }
}, [isServiceAgentModalVisible]);

// Add this function near your other handler functions
const handleAddComment = async () => {
    if (!newComment.trim()) {
        message.warning('Please enter a comment');
        return;
    }

    setAddingComment(true);
    try {
        const payload = {
            leadPageComment: [
                ...allComments,
                {
                    comment: newComment.trim(),
                    changedAt: new Date().toISOString()
                }
            ]
        };

        const response = await API.UpdateLead(lead._id, payload);

        if (response?.status) {
            message.success('Comment added successfully!');
            setNewComment('');
            // Refresh the lead data
            if (onRefresh) onRefresh();
            // If modal is open, update local state
            setAllComments([...allComments, {
                comment: newComment.trim(),
                changedAt: new Date().toISOString()
            }]);
        } else {
            message.error(response?.message || 'Failed to add comment');
        }
    } catch (error) {
        console.error('Error adding comment:', error);
        message.error('Failed to add comment');
    } finally {
        setAddingComment(false);
    }
};
// Add this function near your other formatting functions
const formatCommentDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};
const handleAssignServiceAgent = async () => {
    if (!selectedServiceAgentId) {
        message.warning('Please select a service agent');
        return;
    }

    setAssigningServiceAgent(true);
    try {
        const payload = {
            serviceHandledBy: selectedServiceAgentId
        };
        const response = await API.UpdateLead(lead._id, payload);
        
        if (response?.status) {
            message.success(serviceAgentName ? 'Service agent changed successfully!' : 'Service agent assigned successfully!');
            setIsServiceAgentModalVisible(false);
            setSelectedServiceAgentId(null);
            if (onRefresh) onRefresh();
        } else {
            message.error(response?.message || 'Failed to assign service agent');
        }
    } catch (error) {
        console.error('Error assigning service agent:', error);
        message.error('Failed to assign service agent');
    } finally {
        setAssigningServiceAgent(false);
    }
};

const handleRemoveServiceAgent = async () => {
    Popconfirm.confirm({
        title: 'Remove Service Agent',
        description: 'Are you sure you want to remove the service agent from this lead?',
        onConfirm: async () => {
            setAssigningServiceAgent(true);
            try {
                const payload = { serviceHandledBy: null };
                const response = await API.UpdateLead(lead._id, payload);
                
                if (response?.status) {
                    message.success('Service agent removed successfully!');
                    if (onRefresh) onRefresh();
                } else {
                    message.error(response?.message || 'Failed to remove service agent');
                }
            } catch (error) {
                console.error('Error removing service agent:', error);
                message.error('Failed to remove service agent');
            } finally {
                setAssigningServiceAgent(false);
            }
        }
    });
};
// Warranty Save Handler
const handleSaveWarranty = async () => {
  if (!warrantyValue) {
    message.warning('Please select a warranty option');
    return;
  }

  setSavingWarranty(true);

  try {
    const payload = {
      warranty: warrantyValue
    };

    const response = await API.UpdateLead(lead._id, payload);

    if (response?.status) {
      message.success('Warranty updated successfully!');
      setIsEditingWarranty(false);
      if (onRefresh) onRefresh();
    } else {
      message.error(response?.message || 'Failed to update warranty');
    }
  } catch (error) {
    console.error('Error saving warranty:', error);
    message.error('Failed to save warranty');
  } finally {
    setSavingWarranty(false);
  }
};

// Cancel warranty edit
const handleCancelWarrantyEdit = () => {
  setWarrantyValue(lead?.warranty || '');
  setIsEditingWarranty(false);
};
  // Unified function to update any vehicle field
  const updateVehicleField = async (fieldName, value, validationFn) => {
    // Run validation if provided
    if (validationFn && !validationFn(value)) {
      return;
    }

    // Set loading state based on field
    const loadingStates = {
      'vin': () => setSavingVin(true),
      'specification': () => setSavingSpec(true)
    };

    if (loadingStates[fieldName]) {
      loadingStates[fieldName]();
    }

    try {
      const payload = {
        vehicleInfo: {
          ...vehicleInfo,
          [fieldName]: value.trim()
        }
      };

      const response = await API.UpdateLead(lead._id, payload);

      if (response?.status) {
        const successMessages = {
          'vin': 'VIN updated successfully!',
          'specification': 'Specification updated successfully!'
        };

        message.success(successMessages[fieldName] || 'Updated successfully!');

        // Close appropriate edit mode
        if (fieldName === 'vin') setIsEditingVin(false);
        if (fieldName === 'specification') setIsEditingSpec(false);

        if (onRefresh) onRefresh();
      } else {
        message.error(response?.message || `Failed to update ${fieldName}`);
      }
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      message.error(`Failed to save ${fieldName}`);
    } finally {
      // Reset appropriate loading state
      if (fieldName === 'vin') setSavingVin(false);
      if (fieldName === 'specification') setSavingSpec(false);
    }
  };

  // VIN Save Handler
  const handleSaveVin = () => {
    const validateVIN = (vin) => {
      if (!vin.trim()) {
        message.warning('Please enter a VIN number');
        return false;
      }
      if (vin.trim().length !== 17) {
        message.warning('VIN must be 17 characters long');
        return false;
      }
      return true;
    };

    updateVehicleField('vin', vinValue, validateVIN);
  };

  // Specification Save Handler
  const handleSaveSpec = () => {
    const trimmedValue = specValue.trim();

    if (trimmedValue.length > 2000) {
      message.warning('Specification cannot exceed 2000 characters');
      return;
    }

    // Save trimmed value
    updateVehicleField('specification', trimmedValue);
  };

  // Cancel handlers
  const handleCancelEdit = () => {
    setVinValue(vehicleInfo?.vin || '');
    setIsEditingVin(false);
  };

  const handleCancelSpecEdit = () => {
    setSpecValue(vehicleInfo?.specification || '');
    setIsEditingSpec(false);
  };


  const formattedPhone = formatUSPhone(personalInfo?.phoneNumber);
  const rawPhoneValue = String(personalInfo?.phoneNumber || '').trim();
  const hasPhoneNumber = Boolean(rawPhoneValue);
  const phoneDisplayValue = formattedPhone.isValid ? formattedPhone.formatted : rawPhoneValue;
  const phoneCopyValue = formattedPhone.isValid ? formattedPhone.e164 : rawPhoneValue;
  const hasPhoneValidationInfo = loginAgent?.role === "ADMIN" && Object.keys(phoneValidation).length > 0;
  const phoneValidationReason = phoneValidation.reason || phoneValidation.reasonCode || "";
  const phoneNumberFormatReason = phoneValidation.reason ? String(phoneValidation.reason).trim() : "";
  const hasPhoneValidationCheckedAt = Boolean(phoneValidation.checkedAt);
  const phoneNumberFormatDisplayValue = phoneNumberFormatReason || (hasPhoneValidationCheckedAt ? "Valid Format" : "N/A");
  const phoneNumberFormatStyleKey = phoneNumberFormatReason ? "message" : hasPhoneValidationCheckedAt ? "valid" : "unknown";
  const isRecoveredFromReviewLead = Boolean(lead?.metadata?.recoveredFromReviewLeadId);
  const shouldShowPhoneValidationReason = Boolean(phoneValidationReason && !isRecoveredFromReviewLead);
  // Get stage color
  const getStageColor = (stage) => {
    const colors = {
      "New": "blue",
      "Contacted": "cyan",
      "Quoted": "gold",
      "Follow-up": "orange",
      "Converted": "green",
      "Lost": "red",
      "Voice Mail": "purple",
      "Unknown": "default",
    };
    return colors[stage] || "default";
  };

  // Get payment status color
  const getPaymentColor = (status) => {
    const colors = {
      "Paid": "success",
      "Pending": "warning",
      "Unpaid": "error",
      "Partially Paid": "processing",
    };
    return colors[status] || "default";
  };

  // Get tier type color
  const getTierColor = (tier) => {
    const colors = {
      "TIER-1": "green",
      "TIER-2": "orange",
      "TIER-3": "red",
      "TIER-4": "volcano",
      "TIER-5": "magenta",
    };
    return colors[tier] || "default";
  };

  const getTierLaneColor = (lane) => {
    const colors = {
      default: "geekblue",
      subset1: "cyan",
      subset2: "purple",
      premium: "gold",
    };

    return colors[lane] || "default";
  };

  // Get lead type color
  const getLeadTypeColor = (type) => {
    const colors = {
      "outbound": "blue",
      "inbound": "green",
      "referral": "purple",
      "organic": "cyan",
    };
    return colors[type] || "default";
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: '2-digit',
    });
  };

  // Format date without time
  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      // You can add a message notification here if needed
      message.success(`${text} copied to clipboard`)
      console.log(`${label} copied to clipboard`);
    });
  };

  // Calculate time since creation
  const getTimeSince = (dateString) => {
    if (!dateString) return "";
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return "Just now";
    }
  };


  const handleTransferSuccess = (updatedLead) => {
    setIsTransferModalVisible(false);


    // Update the agent name in the UI immediately
    if (updatedLead?.handledBy) {
      const newAgentName = `${updatedLead.handledBy.firstName} ${updatedLead.handledBy.lastName}`;
      // If your lead object is passed as prop and you can update it
      if (onRefresh) {
        onRefresh(); // Refresh the whole list
      }
    }
  };


  const handleTransferButtonClick = () => {
    setIsTransferModalVisible(true);
  };

  const normalizeUserId = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return value._id || value.id || null;
    }
    return null;
  };

  const resolveSenderAgentId = () => {
    if (loginAgent?.role === "SALES-AGENT") {
      return loginAgent?.id || null;
    }

    const assignedCandidates = [lead?.handledBy, lead?.agentId, lead?.assignedTo];

    for (const candidate of assignedCandidates) {
      if (!candidate) continue;
      if (typeof candidate === "object" && candidate.role && candidate.role !== "SALES-AGENT") {
        continue;
      }

      const resolvedId = normalizeUserId(candidate);
      if (resolvedId) {
        return resolvedId;
      }
    }

    return null;
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalVisible(false);
    setSenderAgentId(null);
  };

  const handleOpenEmailModal = (e) => {
    e.stopPropagation();

    const resolvedSenderAgentId = resolveSenderAgentId();

    if (!resolvedSenderAgentId) {
      message.warning("Please assign a sales agent before sending email.");
      return;
    }

    setSenderAgentId(resolvedSenderAgentId);
    setIsEmailModalVisible(true);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();

    try {
      const scrollContainer = document.querySelector('.ant-layout-content');
      const scrollTop = scrollContainer?.scrollTop ?? window.scrollY ?? 0;
      sessionStorage.setItem('my-leads:scroll-top', String(scrollTop));
      sessionStorage.setItem('my-leads:should-restore', 'true');
    } catch (error) {
      console.error('Failed to save MyLeads navigation state', error);
    }

    onBeforeNavigateToLeadDetails?.();

    navigate(`/lead-details/${lead._id}`, {
      state: { from: '/my-leads', restoreMyLeads: true }
    });
  };




  const isMyLead =
    lead?.handledBy?._id?.toString() === loginAgent?.id?.toString();

  return (
    <Card
      className="lead-card"
      style={{
        borderRadius: 12,
        marginBottom: 16,
        border: isGlobalSearchResult
          ? '2px solid #1890ff'
          : '1px solid #e8e8e8',
        boxShadow: isGlobalSearchResult
          ? '0 4px 12px rgba(24, 144, 255, 0.15)'
          : '0 2px 8px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(to right, #ffffff 0%, #fafafa 100%)',
        ':hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          borderColor: '#1890ff',
        },
      }}
    >
      {/* Search Mode Indicator */}
      {isGlobalSearchResult && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #1890ff, #52c41a)',
          zIndex: 1,
        }} />
      )}

      {/* Main Content */}
      <div style={{ padding: '16px 20px' }}>
        <Row gutter={24} align="middle">
          {/* Left Column - Contact Info */}
          <Col xs={24} md={8} style={{ borderRight: '1px solid #f0f0f0', paddingRight: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Contact Details */}
             
              <div style={{ flex: 1 }}>
{lead?.orderNumber && (
  <div style={{ 
    marginBottom: 8, 
    display: 'flex', 
    alignItems: 'center', 
    gap: 8,
    backgroundColor: '#52c41a',
    padding: '4px 10px',
    borderRadius: '20px',
    width: 'fit-content'
  }}>
    <FileTextOutlined style={{ color: 'white' }} />
    <Text strong style={{ color: 'white', fontSize: 13 }}>
      Order: {lead.orderNumber}
    </Text>
    <Tooltip title="Copy Order Number">
      <Button
        type="text"
        size="small"
        icon={<CopyOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(lead.orderNumber, 'Order Number');
        }}
        style={{
          padding: 0,
          height: 20,
          width: 20,
          minWidth: 20,
          color: 'white',
        }}
      />
    </Tooltip>
  </div>
)}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Title level={5} style={{ margin: 0, color: '#1f2937' }}>
                    {personalInfo.fullName || "Unknown Name"}
                  </Title>
                  {personalInfo.fullName && (
                    <Tooltip title="Copy Name">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(personalInfo.fullName, 'Name');
                        }}
                        style={{
                          padding: 0,
                          height: 20,
                          width: 20,
                          minWidth: 20,
                          color: '#6b7280',
                        }}
                      />
                    </Tooltip>
                  )}
                  {customerTz && (
                    <Tooltip
                      title={
                        customerTz.source === 'zip'
                          ? `Customer local time (from ZIP ${customerTz.zip})`
                          : `Customer local time (from browser: ${customerTz.zone})`
                      }
                    >
                      <Text
                        style={{
                          fontSize: 16, // same as the name (Title level 5 = 16px)
                          fontWeight: 700,
                          color: zoneColor(customerTzAbbr),
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}
                      >
                        {customerTzAbbr} · {customerTzTime}
                      </Text>
                    </Tooltip>
                  )}
                </div>

                {/* Phone Number with Call Button */}
                {hasPhoneNumber && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PhoneOutlined style={{ color: '#1890ff' }} />
                    <Text style={{ color: '#4b5563', fontSize: 14 }}>
                      {phoneDisplayValue}
                    </Text>
                    <Tooltip title="Copy Phone">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(phoneCopyValue, 'Phone');
                        }}
                        style={{
                          padding: 0,
                          height: 20,
                          width: 20,
                          minWidth: 20,
                          color: '#6b7280',
                        }}
                      />
                    </Tooltip>
                    {formattedPhone.isValid && (
                      <a
                        href={`tel:${formattedPhone.e164}`}

                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          type="primary"
                          size="small"
                          icon={<PhoneOutlined />}
                          style={{
                            padding: '0 8px',
                            height: 24,
                            fontSize: 12,
                            background: '#1890ff',
                          }}
                        >
                          Call
                        </Button>
                      </a>
                    )}
                  </div>
                )}

                {hasPhoneValidationInfo && (
                  <div style={phoneValidationContainerStyle}>
                    <div style={phoneValidationFormatRowStyle}>
                      <InfoCircleOutlined style={{ color: '#64748b', fontSize: 11, marginTop: 4, flex: '0 0 auto' }} />
                      <Tag
                        style={{
                          ...getPhoneValidationBadgeStyle(phoneNumberFormatStyleKey, NUMBER_FORMAT_BADGE_STYLES, 'valid'),
                          ...phoneValidationFormatBadgeStyle,
                        }}
                      >
                        Number Format: {phoneNumberFormatDisplayValue}
                      </Tag>
                    </div>
                    <div style={phoneValidationCompactBadgeRowStyle}>
                      {shouldShowPhoneValidationReason && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Status: {phoneValidationReason}
                        </Text>
                      )}
                    </div>
                  </div>
                )}

                {/* Email */}
                {personalInfo.email && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MailOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                    <a
                      href={`mailto:${personalInfo.email}`}
                      style={{
                        color: '#2563eb',
                        fontSize: 13,
                        textDecoration: 'none',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {personalInfo.email}
                    </a>
                    <Tooltip title="Copy Email">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(personalInfo.email, 'Email');
                        }}
                        style={{
                          padding: 0,
                          height: 20,
                          width: 20,
                          minWidth: 20,
                          color: '#6b7280',
                        }}
                      />
                    </Tooltip>
                  </div>
                )}

                {/* Domain above ZIP Code */}
                {loginAgent?.role === "ADMIN" && domain && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GlobalOutlined style={{ color: '#8b5cf6', fontSize: 12 }} />
                    <Text style={{ color: '#6b7280', fontSize: 12 }}>
                      Domain: {domain}
                    </Text>
                  </div>
                )}

                {isInboundLead && createdByName && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserOutlined style={{ color: '#0f766e', fontSize: 12 }} />
                    <Text style={{ color: '#6b7280', fontSize: 12 }}>
                      Created By: {createdByName}
                    </Text>
                  </div>
                )}

                {/* Location */}
                {personalInfo.zipCode && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EnvironmentOutlined style={{ color: '#10b981', fontSize: 12 }} />
                    <Text style={{ color: '#6b7280', fontSize: 12 }}>
                      ZIP: {personalInfo.zipCode}
                    </Text>
                  </div>
                )}

                {/* Tier Type (always visible) */}
                {loginAgent?.role === "ADMIN" && tierType && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CrownOutlined style={{ color: getTierColor(tierType), fontSize: 12 }} />
                    <Space size={6} wrap>
                      <Tag
                        color={getTierColor(tierType)}
                        style={{
                          borderRadius: 12,
                          fontSize: 11,
                          padding: '2px 8px',
                          fontWeight: 'bold',
                        }}
                      >
                        {tierType}
                      </Tag>
                      {tierType === "TIER-1" && tierLaneLabel && (
                        <Tag
                          color={getTierLaneColor(tierLane)}
                          style={{
                            borderRadius: 12,
                            fontSize: 11,
                            padding: '2px 8px',
                            fontWeight: 'bold',
                          }}
                        >
                          {tierLaneDisplayLabel}
                        </Tag>
                      )}
                    </Space>
                  </div>
                )}
 <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
  <TagOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                     {leadType}
                  </div>
                {/* Time Since */}
                {createdAt && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11, color: '#9ca3af' }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      Created {getTimeSince(createdAt)}
                    </Text>
                  </div>
                )}
              </div>
            </div>

            {/* Tags Section */}
            <div style={{ marginTop: 16 }}>
              <Space size={6} wrap>
                {isExistingCustomer && (
                  <Tag
                    color="purple"
                    icon={<StarOutlined />}
                    style={{
                      borderRadius: 12,
                      fontSize: 11,
                      padding: '2px 8px',
                    }}
                  >
                    Existing Customer
                  </Tag>
                )}
                  <Tag
                    color="cyan"
                    icon={<HistoryOutlined />}
                    style={{
                      borderRadius: 12,
                      fontSize: 11,
                      padding: '2px 8px',
                    }}
                  >
                    {lead.status}
                  </Tag>
                {isGlobalSearchResult && (
                  <Tag
                    color="blue"
                    icon={<SearchOutlined />}
                    style={{
                      borderRadius: 12,
                      fontSize: 11,
                      padding: '2px 8px',
                      border: '1px dashed #1890ff',
                    }}
                  >
                    Search Match
                  </Tag>
                )}
              </Space>
            </div>
          </Col>

          {/* Middle Column - Vehicle & Details */}
          <Col xs={24} md={10} style={{ borderRight: '1px solid #f0f0f0', padding: '0 24px' }}>
            {/* Vehicle Info */}
            {(vehicleInfo.year || vehicleInfo.make || vehicleInfo.model) && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <CarOutlined style={{ color: '#059669', fontSize: 16 }} />
                  <Title level={5} style={{ margin: 0, color: '#1f2937' }}>
                    Vehicle Details
                  </Title>

                </div>

                <div style={{
                  background: '#f8fafc',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #e5e7eb'
                }}>
                  <Row gutter={12}>
                    <Col span={6}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>
                            Year
                          </Text>
                          <Text style={{ fontSize: 14, color: '#111827' }}>
                            {vehicleInfo.year || 'N/A'}
                          </Text>
                        </div>
                        {vehicleInfo.year && (
                          <Tooltip title="Copy Year">
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(vehicleInfo.year, 'Year');
                              }}
                              style={{
                                padding: 0,
                                height: 16,
                                width: 16,
                                minWidth: 16,
                                color: '#9ca3af',
                                fontSize: 10,
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>
                            Make
                          </Text>
                          <Text style={{ fontSize: 14, color: '#111827' }}>
                            {vehicleInfo.make || 'N/A'}
                          </Text>
                        </div>
                        {vehicleInfo.make && (
                          <Tooltip title="Copy Make">
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(vehicleInfo.make, 'Make');
                              }}
                              style={{
                                padding: 0,
                                height: 16,
                                width: 16,
                                minWidth: 16,
                                color: '#9ca3af',
                                fontSize: 10,
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>
                            Model
                          </Text>
                          <Text style={{ fontSize: 14, color: '#111827' }}>
                            {vehicleInfo.model || 'N/A'}
                          </Text>
                        </div>
                        {vehicleInfo.model && (
                          <Tooltip title="Copy Model">
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(vehicleInfo.model, 'Model');
                              }}
                              style={{
                                padding: 0,
                                height: 16,
                                width: 16,
                                minWidth: 16,
                                color: '#9ca3af',
                                fontSize: 10,
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>
                            Engine
                          </Text>
                          <Text style={{ fontSize: 14, color: '#111827' }}>
                            {vehicleInfo.part || 'N/A'}
                          </Text>
                        </div>
                        {vehicleInfo.part && (
                          <Tooltip title="Copy Engine">
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(vehicleInfo.part, 'Engine');
                              }}
                              style={{
                                padding: 0,
                                height: 16,
                                width: 16,
                                minWidth: 16,
                                color: '#9ca3af',
                                fontSize: 10,
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                    <div>
                    <Text strong style={{ fontSize: 11, color: '#6b7280', marginTop: 1.5 }}>
                      VIN # :
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isEditingVin ? (
                        // EDIT MODE
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Input
                            value={vinValue}
                            onChange={(e) => setVinValue(e.target.value.toUpperCase())}
                            placeholder="Enter VIN"
                            maxLength={17}
                            style={{
                              width: 140,
                              fontSize: 11,
                              height: 24,
                              padding: '2px 6px',

                            }}
                            autoFocus
                          />
                          <Button
                            type="primary"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={handleSaveVin}
                            loading={savingVin}
                            disabled={vinValue.length !== 17}
                            style={{
                              padding: '0 6px',
                              height: 24,
                              fontSize: 10,

                            }}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleCancelEdit}
                            disabled={savingVin}
                            style={{
                              padding: '0 6px',
                              height: 24,
                              fontSize: 10,
                              color: '#9ca3af'
                            }}
                          />
                        </div>
                      ) : (
                        // VIEW MODE
                        <>
                          <Tag
                            color="blue"
                            icon={<NumberOutlined />}
                            style={{
                              fontSize: 11,
                              borderRadius: 12,
                              marginTop: 4,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              if (loginAgent?.role === "ADMIN") {
                                setIsEditingVin(true);
                                setVinValue(vehicleInfo.vin);
                              }
                            }}
                          >
                            {vehicleInfo.vin ? vehicleInfo.vin : "Add VIN Number"}
                          </Tag>

                          <>
                            <Tooltip title="Edit VIN">
                              <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsEditingVin(true);
                                  setVinValue(vehicleInfo.vin);
                                }}
                                style={{
                                  padding: 0,
                                  height: 16,
                                  width: 16,
                                  minWidth: 16,
                                  color: '#9ca3af',
                                  fontSize: 10,
                                }}
                              />
                            </Tooltip>

                          </>

                          {vehicleInfo.vin && (
                            <Tooltip title="Copy VIN">
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(vehicleInfo.vin, 'VIN');
                                }}
                                style={{
                                  padding: 0,
                                  height: 16,
                                  width: 16,
                                  minWidth: 16,
                                  color: '#9ca3af',
                                  fontSize: 10,
                                }}
                              />
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
</div>
                    <div>
                      <Text strong style={{ fontSize: 11, color: '#6b7280', marginTop: 1.5 }}>
    Warranty :
  </Text>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    {isEditingWarranty ? (
      // EDIT MODE
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <AutoComplete
          value={warrantyValue}
          onChange={(value) => setWarrantyValue(value)}
          placeholder="Select or enter warranty"
          style={{
            width: 140,
            fontSize: 11,
          }}
          size="small"
          autoFocus
          allowClear
          options={[
            { value: '30 Days', label: '30 Days' },
            { value: '60 Days', label: '60 Days' },
            { value: '90 Days', label: '90 Days' },
            { value: '120 Days', label: '120 Days' },
            { value: '150 Days', label: '150 Days' },
            { value: '180 Days', label: '180 Days' },
            { value: '1 Year', label: '1 Year' },
            { value: '2 Years', label: '2 Years' },
            { value: '3 Years', label: '3 Years' },
            { value: '4 Years', label: '4 Years' },
            { value: '5 Years', label: '5 Years' },
            { value: 'No Warranty', label: 'No Warranty' },
          ]}
          filterOption={(inputValue, option) =>
            option?.label?.toLowerCase().includes(inputValue.toLowerCase())
          }
        />
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          onClick={handleSaveWarranty}
          loading={savingWarranty}
          disabled={!warrantyValue}
          style={{
            padding: '0 6px',
            height: 24,
            fontSize: 10,
          }}
        />
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={handleCancelWarrantyEdit}
          disabled={savingWarranty}
          style={{
            padding: '0 6px',
            height: 24,
            fontSize: 10,
            color: '#9ca3af'
          }}
        />
      </div>
    ) : (
      // VIEW MODE
      <>
        <Tag
          color={getWarrantyColor(lead?.warranty)}
          icon={<SecurityScanOutlined />}
          style={{
            fontSize: 11,
            borderRadius: 12,
            marginTop: 4,
            cursor: (loginAgent?.role === "ADMIN" || loginAgent?.role === "SALES-AGENT") ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (loginAgent?.role === "ADMIN" || loginAgent?.role === "SALES-AGENT") {
              setIsEditingWarranty(true);
              setWarrantyValue(lead?.warranty || '');
            }
          }}
        >
          {lead?.warranty ? lead.warranty : "Add Warranty"}
        </Tag>

        {(loginAgent?.role === "ADMIN" || loginAgent?.role === "SALES-AGENT") && (
          <Tooltip title="Edit Warranty">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingWarranty(true);
                setWarrantyValue(lead?.warranty || '');
              }}
              style={{
                padding: 0,
                height: 16,
                width: 16,
                minWidth: 16,
                color: '#9ca3af',
                fontSize: 10,
              }}
            />
          </Tooltip>
        )}

        {lead?.warranty && (
          <Tooltip title="Copy Warranty">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(lead.warranty, 'Warranty');
              }}
              style={{
                padding: 0,
                height: 16,
                width: 16,
                minWidth: 16,
                color: '#9ca3af',
                fontSize: 10,
              }}
            />
          </Tooltip>
        )}
      </>
    )}
  </div>
  </div>
                  </div>


                  {/* Specification Field */}
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                    <Text strong style={{ fontSize: 11, color: '#6b7280', marginTop: 1.5 }}>
                      Specification :
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isEditingSpec ? (
                        // EDIT MODE
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Input.TextArea
                            className="mb-1"
                            value={specValue}
                            onChange={(e) => setSpecValue(e.target.value)}
                            placeholder="Enter specification"
                            maxLength={2000}
                            showCount={{
                              formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
                              show: true
                            }}
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            style={{
                              width: 250,
                              fontSize: 11,
                              minHeight: 24,
                              padding: '2px 6px',

                            }}
                            autoFocus
                          />
                          <Button
                            type="primary"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={handleSaveSpec}
                            loading={savingSpec}
                            disabled={specValue.length > 2000}
                            style={{
                              padding: '0 6px',
                              height: 24,
                              fontSize: 10,
                            }}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleCancelSpecEdit}
                            disabled={savingSpec}
                            style={{
                              padding: '0 6px',
                              height: 24,
                              fontSize: 10,
                              color: '#9ca3af'
                            }}
                          />
                        </div>
                      ) : (
                        // VIEW MODE
                        <>
                          <Tooltip
                            title={
                              <div style={{
                                maxWidth: 500,
                                maxHeight: 300,
                                overflow: 'auto',
                                padding: '8px 0'
                              }}>
                                <div style={{
                                  padding: '0 8px',
                                  fontSize: 14,
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  color: '#ffffff' // White text
                                }}>
                                  {vehicleInfo.specification || "No specification added"}
                                </div>
                              </div>
                            }
                            placement="top"
                            color="#000000" // Black background
                          >
                            <Tag
                              color="green"
                              icon={<FileTextOutlined />}
                              style={{
                                fontSize: 11,
                                borderRadius: 12,
                                marginTop: 4,
                                cursor: 'pointer',
                                maxWidth: 180,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              onClick={() => {
                                if (loginAgent?.role === "ADMIN" || loginAgent?.role === "SALES-AGENT") {
                                  setIsEditingSpec(true);
                                  setSpecValue(vehicleInfo.specification || '');
                                }
                              }}
                            >
                              {vehicleInfo.specification ?
                                (vehicleInfo.specification.length > 20
                                  ? `${vehicleInfo.specification.substring(0, 20)}...`
                                  : vehicleInfo.specification
                                )
                                : "Add Specification"
                              }
                            </Tag>
                          </Tooltip>

                          {/* Rest of your buttons remain outside the tooltip */}
                          {(loginAgent?.role === "ADMIN" || loginAgent?.role === "SALES-AGENT") && (
                            <Tooltip title="Edit Specification">
                              <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsEditingSpec(true);
                                  setSpecValue(vehicleInfo.specification || '');
                                }}
                                style={{
                                  padding: 0,
                                  height: 16,
                                  width: 16,
                                  minWidth: 16,
                                  color: '#9ca3af',
                                  fontSize: 10,
                                }}
                              />
                            </Tooltip>
                          )}

                          {vehicleInfo.specification && (
                            <Tooltip title="Copy Specification">
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(vehicleInfo.specification, 'Specification');
                                }}
                                style={{
                                  padding: 0,
                                  height: 16,
                                  width: 16,
                                  minWidth: 16,
                                  color: '#9ca3af',
                                  fontSize: 10,
                                }}
                              />
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status & Comments */}
            <div>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {/* Lead Stage */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <TagOutlined style={{ color: '#8b5cf6', fontSize: 12 }} />
                  <Text strong style={{ fontSize: 12, color: '#6b7280' }}>Status:</Text>
                  <Tag
                    color={getStageColor(leadStage)}
                    style={{
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 'bold',
                      marginInlineEnd: 0,
                    }}
                  >
                    {leadStage}
                  </Tag>
                  {followUpAt && (
                    <Text style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      {followUpAt} {zoneAbbrPT(lead.followUpDate)}
                      {isFollowUpOverdue && (
                        <span style={{ color: '#ff4d4f', fontWeight: 600 }}> · Overdue</span>
                      )}
                    </Text>
                  )}
                </div>

         {/* Latest Comment */}
{latestComment && (
    <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        background: '#fef3c7',
        padding: 8,
        borderRadius: 6,
        border: '1px solid #fde68a'
    }}>
        <CommentOutlined style={{ color: '#d97706', marginTop: 2 }} />
        <Text
            style={{
                fontSize: 11,
                color: '#92400e',
                flex: 1,
                lineHeight: 1.4,
            }}
            ellipsis={{ tooltip: latestComment }}
        >
            <strong>Latest Note:</strong> {latestComment}
        </Text>
        {loginAgent?.role === "ADMIN" && (
            <Tooltip title="Copy Comment">
                <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(latestComment, 'Comment');
                    }}
                    style={{
                        padding: 0,
                        height: 16,
                        width: 16,
                        minWidth: 16,
                        color: '#d97706',
                        fontSize: 10,
                    }}
                />
            </Tooltip>
        )}
    </div>
)}

    <>
          {/* Show Latest Comment */}
        {allComments.length > 0 && (
            <div style={{
                marginTop: 12,
                padding: '8px 12px',
                background: '#e6f7ff',
                borderRadius: 8,
                border: '1px solid #91d5ff',
                position: 'relative'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 6
                }}>
                    <Space size={4}>
                        <CommentOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                        <Text strong style={{ fontSize: 11, color: '#0050b3' }}>
                            Latest Comment
                        </Text>
                        <Tag color="blue" style={{ fontSize: 10, borderRadius: 10, padding: '0 6px', margin: 0 }}>
                            {formatCommentDate(allComments[allComments.length - 1]?.changedAt)}
                        </Tag>
                    </Space>
                    <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => setIsCommentsModalVisible(true)}
                        style={{ padding: 0, height: 20, fontSize: 11, color: '#1890ff' }}
                    >
                        View All ({allComments.length})
                    </Button>
                </div>
                <Text style={{ fontSize: 12, color: '#003a8c', display: 'block', lineHeight: 1.5 }}>
                    {allComments[allComments.length - 1]?.comment}
                </Text>
                <div style={{ marginTop: 6 }}>
                    <Tooltip title="Copy Comment">
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(allComments[allComments.length - 1]?.comment, 'Comment');
                            }}
                            style={{ padding: 0, height: 16, width: 16, color: '#1890ff' }}
                        />
                    </Tooltip>
                </div>
            </div>
        )}
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 8
        }}>
            <Input.TextArea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                autoSize={{ minRows: 1, maxRows: 3 }}
                style={{
                    fontSize: 12,
                    borderRadius: 6,
                    resize: 'none',
                    flex: 1
                }}
                onPressEnter={(e) => {
                    if (e.shiftKey) {
                        return;
                    }
                    e.preventDefault();
                    handleAddComment();
                }}
            />
            
            <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
                onClick={handleAddComment}
                loading={addingComment}
                disabled={!newComment.trim()}
            >
                Post
            </Button>
        </div>

  
    </>

       </Space>
            </div>
          </Col>

          {/* Right Column - Pricing & Actions */}
          <Col xs={24} md={6}>
            {/* Pricing Info */}
            {showQuotedAmount && (
              <div style={{ marginBottom: 20, textAlign: 'center' }}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: 11, color: '#6b7280' }}>Quoted Amount</Text>
                  <Title level={3} style={{ margin: '4px 0', color: '#111827' }}>
                    ${totalAmount.toLocaleString()}
                  </Title>
                </div>

                {showPaymentTag && (
                  <Tag
                    color={getPaymentColor(paymentStatus)}
                    icon={paymentStatus === "Paid" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    style={{
                      fontSize: 11,
                      borderRadius: 12,
                      padding: '4px 12px',
                      fontWeight: 'bold',
                    }}
                  >
                    {paymentStatus}
                  </Tag>
                )}

                {quotedPrice && quotedPrice !== totalAmount && (
                  <Text
                    style={{
                      display: 'block',
                      marginTop: 4,
                      fontSize: 11,
                      color: '#6b7280'
                    }}
                  >
                    <DollarCircleOutlined style={{ marginRight: 4 }} />
                    Quoted: ${quotedPrice.toLocaleString()}
                  </Text>
                )}
              </div>
            )}

            {/* Dates */}
            <div style={{ marginBottom: 20 }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {createdAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined style={{ color: '#6b7280', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, color: '#000000' }}>
                      <strong style={{ fontWeight: 600 }}>Created:</strong>{' '}
                      <span style={{ color: '#4b5563' }}>{formatDate(createdAt)}</span>
                    </Text>
                  </div>
                )}
                {updatedAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ClockCircleOutlined style={{ color: '#6b7280', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, color: '#000000' }}>
                      <strong style={{ fontWeight: 600 }}>Updated:</strong>{' '}
                      <span style={{ color: '#4b5563' }}>{formatDate(updatedAt)}</span>
                    </Text>
                  </div>
                )}
                {salesMadeDate && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#16a34a', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, color: '#000000' }}>
                      <strong style={{ fontWeight: 600 }}>Sales Made:</strong>{' '}
                      <span style={{ color: '#4b5563' }}>{formatDate(salesMadeDate)}</span>
                    </Text>
                  </div>
                )}
                {/* Queued At below Updated */}
                {queuedAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <InboxOutlined style={{ color: '#6b7280', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, color: '#000000' }}>
                      <strong style={{ fontWeight: 600 }}>Queued At:</strong>{' '}
                      <span style={{ color: '#4b5563' }}>{formatDate(queuedAt)}</span>
                    </Text>
                  </div>
                )}
                 {assignedDate && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserSwitchOutlined style={{ color: '#6b7280', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, color: '#000000' }}>
                      <strong style={{ fontWeight: 600 }}>Assign At:</strong>{' '}
                      <span style={{ color: '#4b5563' }}>{formatDate(assignedDate)}</span>
                    </Text>
                  </div>
                )}
                {/* Agent Name (always visible) */}
                {agentName && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar
                      size={20}
                      style={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        fontSize: 10,
                        fontWeight: 'bold',
                      }}
                    >
                      {agentName?.[0]?.toUpperCase()}
                    </Avatar>
                  <Text style={{ fontSize: 12, color: '#000000', fontWeight: 400 }}>
                                   <strong style={{ fontWeight: 500 }}>Sales Agent:</strong>{' '}
         {agentName}
                    </Text>
                    <Tooltip title="Copy Agent Name">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(agentName, 'Agent Name');
                        }}
                        style={{
                          padding: 0,
                          height: 18,
                          width: 18,
                          minWidth: 18,
                          color: '#9ca3af',
                          fontSize: 10,
                        }}
                      />
                    </Tooltip>
                  </div>
                )}
                 {(serviceAgentName || lead?.serviceHandledBy) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                    size={20}
                    style={{
                        background: 'linear-gradient(135deg, #52c41a, #389e0d)',
                        fontSize: 10,
                        fontWeight: 'bold',
                    }}
                >
                    {loadingServiceAgent ? (
                        <SyncOutlined spin style={{ fontSize: 10 }} />
                    ) : (
                        (serviceAgentName?.[0]?.toUpperCase() || 'S')
                    )}
                </Avatar>
                  <Text style={{ fontSize: 12, color: '#000000', fontWeight: 400 }}>
                    <strong style={{ fontWeight: 500 }}>Service Agent:</strong>{' '}
                        {loadingServiceAgent ? 'Loading...' : (serviceAgentName || 'Service Agent')}
                </Text>
                
                {serviceAgentName && !loadingServiceAgent && (
                    <Tooltip title="Copy Service Agent Name">
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(serviceAgentName, 'Service Agent Name');
                            }}
                            style={{
                                padding: 0,
                                height: 18,
                                width: 18,
                                minWidth: 18,
                                color: '#9ca3af',
                                fontSize: 10,
                            }}
                        />
                    </Tooltip>
                )}
          
            </div>
        )}
              </Space>
            </div>

       {/* Action Buttons */}
<Space
    size={8}
    style={{
        width: '100%',
        justifyContent: 'flex-end',
        minHeight: 40,
    }}
>
    {/* Service Agent Assignment Button - Show for ADMIN and SALES-AGENT */}
    {(loginAgent?.role === "ADMIN" || (loginAgent?.role === "SALES-AGENT" && isMyLead)) && (
        <Tooltip title={serviceAgentName ? "Change Service Agent" : "Assign Service Agent"}>
            <Button
                type="default"
                shape="circle"
                icon={<TeamOutlined />}
                size="middle"
                onClick={() => {
                    // Open service agent assignment modal
                    setIsServiceAgentModalVisible(true);
                }}
                style={{
                    color: serviceAgentName ? '#52c41a' : '#fa8c16',
                    borderColor: serviceAgentName ? '#b7eb8f' : '#ffd591',
                    backgroundColor: serviceAgentName ? '#f6ffed' : '#fff7e6',
                }}
            />
        </Tooltip>
    )}

    {(loginAgent?.role === "ADMIN" || (loginAgent?.role === "SALES-AGENT" && isMyLead)) && (
        <Tooltip title="Transfer / Re-assign Lead">
            <Button
                type="default"
                shape="circle"
                icon={<SwapOutlined />}
                size="middle"
                onClick={() => setIsTransferModalVisible(true)}
                style={{
                    color: loginAgent?.role === "ADMIN" ? '#722ed1' : '#fa8c16',
                    borderColor: loginAgent?.role === "ADMIN" ? '#d3adf7' : '#ffd591',
                }}
            />
        </Tooltip>
    )}

    {(!isGlobalSearchResult ||
        loginAgent?.role === "ADMIN" ||
        isMyLead) && (
            <Tooltip title="View Details">
                <Button
                    type="default"
                    shape="circle"
                    icon={<EyeOutlined />}
                    size="middle"
                    onClick={handleViewDetails}
                    style={{
                        borderColor: isGlobalSearchResult ? '#1890ff' : '#d1d5db',
                        color: isGlobalSearchResult ? '#1890ff' : '#6b7280',
                    }}
                />
            </Tooltip>
        )}

    {/* Browser / ipwhois / timezone-check detail - ADMIN only, matching the
        isAdmin gate on the same data in LeadDetails DetailsTab. */}
    {isAdminUser && (
        <Tooltip title="View More Detail">
            <Button
                type="default"
                shape="circle"
                icon={<DatabaseOutlined />}
                size="middle"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsFullDetailModalVisible(true);
                }}
                style={{
                    borderColor: '#13c2c2',
                    color: '#13c2c2',
                }}
            />
        </Tooltip>
    )}

    {(!isGlobalSearchResult ||
        loginAgent?.role === "ADMIN" ||
        isMyLead) &&
        personalInfo.email && (
            <Tooltip title="Send Email">
                <Button
                    type="default"
                    shape="circle"
                    icon={<SendOutlined />}
                    size="middle"
                    onClick={handleOpenEmailModal}
                    style={{
                        borderColor: '#f59e0b',
                        color: '#f59e0b',
                    }}
                />
            </Tooltip>
        )}

    {/* Delete - only admin, always */}
    {loginAgent?.role === "ADMIN" && (
        <Tooltip title="Delete Lead">
            <Popconfirm
                title="Move to Recycle Bin?"
                description="This action will move the lead to the recycle bin."
                onConfirm={(e) => {
                    e?.stopPropagation();
                    onDelete(lead._id);
                }}
                okText="Yes"
                cancelText="No"
                placement="topRight"
            >
                <Button
                    danger
                    shape="circle"
                    icon={<DeleteOutlined />}
                    size="middle"
                    onClick={(e) => e.stopPropagation()}
                />
            </Popconfirm>
        </Tooltip>
    )}
</Space>

          </Col>
        </Row>
      </div>

      {/* Admin Checkbox */}
      {loginAgent?.role === "ADMIN" && (
        <div style={{
          position: "absolute",
          top: 12,
          left: 12,
          zIndex: 10
        }}>
          <Checkbox
            checked={selectedRowKeys.includes(lead._id)}
            onChange={(e) => {
              e.stopPropagation();
              if (e.target.checked) {
                setSelectedRowKeys((prev) => [...prev, lead._id]);
              } else {
                setSelectedRowKeys((prev) => prev.filter((id) => id !== lead._id));
              }
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              transform: 'scale(1.2)',
            }}
          />
        </div>
      )}
      <TransferLeadModal
        visible={isTransferModalVisible}
        onCancel={() => setIsTransferModalVisible(false)}
        onTransfer={handleTransferSuccess}
        lead={lead}
        currentAgentId={lead?.handledBy}
        agentMap={agentMap}
        onRefresh={onRefresh} 
      />
      <EmailTemplateModal
        visible={isEmailModalVisible}
        onCancel={handleCloseEmailModal}
        onSend={handleCloseEmailModal}
        lead={lead}
        userData={loginAgent}
        senderAgentId={senderAgentId}
      />
      {/* Service Agent Assignment Modal */}
<Modal
    title={
        <Space>
            <TeamOutlined style={{ color: '#52c41a' }} />
            <span>{serviceAgentName ? 'Change Service Agent' : 'Assign Service Agent'}</span>
        </Space>
    }
    open={isServiceAgentModalVisible}
    onCancel={() => {
        setIsServiceAgentModalVisible(false);
        setSelectedServiceAgentId(null);
    }}
    footer={[
        serviceAgentName && (
            <Button
                key="remove"
                danger
                onClick={handleRemoveServiceAgent}
                loading={assigningServiceAgent}
                icon={<DeleteOutlined />}
            >
                Remove
            </Button>
        ),
        <Button key="cancel" onClick={() => setIsServiceAgentModalVisible(false)}>
            Cancel
        </Button>,
        <Button
            key="assign"
            type="primary"
            onClick={handleAssignServiceAgent}
            loading={assigningServiceAgent}
            icon={<CheckOutlined />}
        >
            {serviceAgentName ? 'Change' : 'Assign'}
        </Button>,
    ]}
    width={500}
>
    <div style={{ padding: '16px 0' }}>
        <p style={{ marginBottom: '16px', color: '#666' }}>
            {serviceAgentName 
                ? `Current Service Agent: ${serviceAgentName}` 
                : 'Select a service agent to handle this lead\'s service and support needs.'}
        </p>
        {serviceAgentName && (
            <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                background: '#f6ffed', 
                borderRadius: '8px',
                border: '1px solid #b7eb8f'
            }}>
                <Text type="secondary">Current:</Text>
                <div style={{ fontWeight: 'bold', marginTop: '4px' }}>
                    <Avatar 
                        size={24} 
                        style={{ 
                            backgroundColor: '#52c41a', 
                            marginRight: '8px',
                            verticalAlign: 'middle'
                        }}
                    >
                        {serviceAgentName?.[0]?.toUpperCase()}
                    </Avatar>
                    {serviceAgentName}
                </div>
                {serviceAgentDetails?.email && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {serviceAgentDetails.email}
                    </div>
                )}
            </div>
        )}
        <Select
            placeholder="Search and select service agent"
            style={{ width: '100%' }}
            size="large"
            value={selectedServiceAgentId}
            onChange={setSelectedServiceAgentId}
            showSearch
            loading={loadingServiceAgents}
            optionFilterProp="children"
            filterOption={(input, option) =>
                (option?.props?.children?.[0]?.props?.children?.[1] || '').toLowerCase().includes(input.toLowerCase())
            }
        >
            {availableServiceAgents.map(agent => (
                <Option key={agent._id} value={agent._id}>
                    <div>
                        <div style={{ fontWeight: 'bold' }}>
                            {`${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.userName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                            {agent.email} | {agent.role?.replace('-', ' ') || 'Service Agent'}
                        </div>
                    </div>
                </Option>
            ))}
        </Select>
        {availableServiceAgents.length === 0 && !loadingServiceAgents && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No service agents available. Please add service agents in the user management.
            </div>
        )}
    </div>
</Modal>
{/* Comments Modal */}
<Modal
    title={
        <Space>
            <CommentOutlined style={{ color: '#1890ff' }} />
            <span>All Comments ({allComments.length})</span>
        </Space>
    }
    open={isCommentsModalVisible}
    onCancel={() => setIsCommentsModalVisible(false)}
    footer={[
        <Button key="close" onClick={() => setIsCommentsModalVisible(false)}>
            Close
        </Button>
    ]}
    width={600}
    bodyStyle={{ maxHeight: '500px', overflowY: 'auto' }}
>
    <div style={{ padding: '8px 0' }}>
        {allComments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <CommentOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <p>No comments yet</p>
            </div>
        ) : (
            <Timeline
                items={[...allComments] // Create a copy of the array
                    .reverse() // Reverse to show latest first
                    .map((comment, index, reversedArray) => ({
                        color: index === 0 ? 'red' : 'blue', // Latest comment in red, others blue
                        children: (
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ 
                                    background: index === 0 ? '#fff1f0' : '#f5f5f5', 
                                    padding: 12, 
                                    borderRadius: 8,
                                    borderLeft: `3px solid ${index === 0 ? '#ff4d4f' : '#1890ff'}`,
                                    border: index === 0 ? '1px solid #ffccc7' : 'none'
                                }}>
                                    {index === 0 && (
                                        <Tag 
                                            color="red" 
                                            style={{ 
                                                marginBottom: 8, 
                                                fontSize: 11,
                                                borderRadius: 12
                                            }}
                                        >
                                            Latest
                                        </Tag>
                                    )}
                                    <div style={{ marginBottom: 8 }}>
                                        <Text style={{ fontSize: 12, color: '#999' }}>
                                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                                            {formatCommentDate(comment.changedAt)}
                                        </Text>
                                    </div>
                                    <Text style={{ fontSize: 14, lineHeight: 1.5 }}>
                                        {comment.comment}
                                    </Text>
                                    <div style={{ marginTop: 8 }}>
                                        <Tooltip title="Copy Comment">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                onClick={() => copyToClipboard(comment.comment, 'Comment')}
                                                style={{ padding: 0, height: 20, width: 20 }}
                                            />
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        )
                    }))}
            />
        )}
    </div>
</Modal>

{isAdminUser && (
    <LeadFullDetailModal
        open={isFullDetailModalVisible}
        onClose={() => setIsFullDetailModalVisible(false)}
        leadId={lead._id}
        source="lead"
    />
)}
    </Card>
  );
}

// Re-renders only when its props change, not whenever My Leads renders (for
// example on every keystroke in the search box).
export default React.memo(LeadCard);
